'use client';

import React, { useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { Button, Errors, Loader } from '@/components';

import {
  CurrentPlayKit,
  NextPlayKit,
  UpcomingPlayKit,
} from '@/components/account';

import {
  buildBannerPropsWithDynamicContent,
  createScheduledItemGroups,
  getNextItemEligibleForPrepayExtension,
  getPrepayBundleOffers,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import { determineMaxSavings } from '@/components/account/NextPlayKit/utils';

import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { CmsBanner } from '@/components/generics/GenBanner';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';

import { useErrorDirectives } from '@/context/ErrorDirectiveContext';

import { ERROR_KINDS } from '@/core/constants';
import { FeatureFlag } from '@/core/featureFlags';
import { TypeBanner } from '@/core/generated-types';

import useFeature from '@/hooks/useFeature';
import useLocale from '@/hooks/useLocale';

import { TypeDecoratedUserRecord } from '@/lib/api/accountManager/decorators/decorateUser';
import { TypeChildRecord } from '@/lib/api/accountManager/types';
import useCustomerOrders from '@/lib/api/orderManager/hooks/useCustomerOrders';
import useDraftOrders from '@/lib/api/orderManager/hooks/useDraftOrders';
import useBundleDiscount from '@/lib/api/productCatalog/hooks/useBundleDiscount';
import usePlayKitBundleOffers from '@/lib/api/productCatalog/hooks/usePlayKitBundleOffers';
import { TypeDecoratedSubscriptionRecord } from '@/lib/api/subscriptionManager/decorators/decorateSubscriptions';

import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import { getPlatform } from '@/utils/helpers';
import {
  CTA_NAMES,
  EXTENSION_UPSELL_EVENT_STEPS,
  trackExtensionFlowStepEvent,
} from '@/utils/rudderstack';
import { trackClickEvent } from '@/components/account/PlayKitSchedule/utils';

import interpolate from '@/utils/translations/interpolate';

import {
  getCurrencyFromLocale,
  getLanguageFromLocale,
} from '@/utils/translations/localizationHelpers';

import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';

export type LayoutActiveSubscriptionProps = {
  t: TypePlayKitPageTranslations;
  selectedChild: TypeChildRecord;
  selectedSubscription: TypeDecoratedSubscriptionRecord;
  user: TypeDecoratedUserRecord;
  noticeBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
  prepayBanner?: TypeBanner;
};

function LayoutActiveSubscription({
  t,
  selectedChild,
  selectedSubscription,
  user,
  noticeBanner,
  prepayBanner,
  holidayBanner,
}: LayoutActiveSubscriptionProps) {
  const [showHiddenKits, setShowHiddenKits] = useState<boolean>(false);

  const { errorDirectives } = useErrorDirectives();

  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const { [FeatureFlag.SUB_SQUAD_CANCEL_SAVER_2348]: cancelSaver2348Variant } =
    useFlags();

  const isCancelSaver2348VariantC = cancelSaver2348Variant === 'c';

  const searchParams = useSearchParams();

  const discountAmount = searchParams?.get('discount_amount');

  const shouldShowDiscountApplied =
    isCancelSaver2348VariantC && discountAmount !== null;

  useEffect(() => {
    // when new child is selected, close upcoming kit drawers
    setShowHiddenKits(false);
  }, [selectedChild]);

  const locale = useLocale();

  const language = getLanguageFromLocale(locale);

  const currency = getCurrencyFromLocale(locale);

  const isBfcmEnabled = useFeature('money_bfcm-mode-2025');

  const { customerOrdersData, customerOrdersLoading, customerOrdersError } =
    useCustomerOrders(selectedSubscription.recipient_uuid);

  const { draftOrdersData, draftOrdersLoading, draftOrdersError } =
    useDraftOrders(
      selectedSubscription.recipient_uuid,
      selectedSubscription.uuid,
    );

  const { bundleDiscountData, bundleDiscountLoading, bundleDiscountError } =
    useBundleDiscount(selectedSubscription.bundle_discount_uuid);

  const nextItemEligibleForPrepayExtension =
    selectedSubscription?.next_kit_anchor ||
    getNextItemEligibleForPrepayExtension(selectedSubscription)?.product
      ?.anchor;

  const isPrepayExtensionDisabled = !nextItemEligibleForPrepayExtension;

  const {
    playKitBundleOffersData,
    playKitBundleOffersLoading,
    playKitBundleOffersError,
  } = usePlayKitBundleOffers({
    language: language,
    currencyCode: currency,
    productAnchor: nextItemEligibleForPrepayExtension,
    shouldFetch: !isPrepayExtensionDisabled,
  });

  if (
    bundleDiscountLoading ||
    draftOrdersLoading ||
    customerOrdersLoading ||
    playKitBundleOffersLoading
  ) {
    return (
      <div data-testid="layout-active-subscription">
        <Loader loading text={t.loading} />
      </div>
    );
  }

  if (
    bundleDiscountError ||
    draftOrdersError ||
    customerOrdersError ||
    playKitBundleOffersError
  ) {
    return (
      <div data-testid="layout-active-subscription">
        <Errors errors={errors} heading={heading} />
      </div>
    );
  }

  const draftOrder = draftOrdersData?.draft_orders?.[0];

  const { currentGroup, nextGroups, upcomingGroups } =
    createScheduledItemGroups({
      subscription: selectedSubscription,
      t,
      bundleDiscountData,
      draftOrderData: draftOrder,
      ordersData: customerOrdersData?.orders,
      userData: user,
    });

  const UPCOMING_KIT_PREVIEW_CUTOFF = 3;

  const hasHiddenUpcomingKits =
    upcomingGroups.length > UPCOMING_KIT_PREVIEW_CUTOFF;

  const visibleUpcomingPlayKits = showHiddenKits
    ? upcomingGroups
    : upcomingGroups.slice(0, UPCOMING_KIT_PREVIEW_CUTOFF);

  const prepayBundleExtensionOffers = getPrepayBundleOffers(
    playKitBundleOffersData,
  );

  const isEligibleForPrepayUpsell =
    !isBfcmEnabled &&
    !selectedSubscription.partial_subscription &&
    prepayBundleExtensionOffers.length > 0;

  const isEligibleForHolidayUpsell =
    isBfcmEnabled &&
    !selectedSubscription.partial_subscription &&
    prepayBundleExtensionOffers.length > 0;

  const shouldShowPrepayBanner =
    isEligibleForPrepayUpsell && prepayBanner && currentGroup;

  const shouldShowHolidayBanner =
    isEligibleForHolidayUpsell && holidayBanner && currentGroup;

  const prepayExtensionFlowLink = currentGroup
    ? constructLegacyCustomerPortalLink(locale, [
        currentGroup.userUuid,
        'subscriptions',
        currentGroup.subscriptionUuid,
        'prepay-extension',
      ])
    : '';

  const maxSavings = determineMaxSavings(prepayBundleExtensionOffers);

  const prepayBannerProps = shouldShowPrepayBanner
    ? buildBannerPropsWithDynamicContent(prepayBanner.fields, {
        linkUrl: prepayExtensionFlowLink,
        headingStringInterpolations: { savings: maxSavings },
      })
    : null;

  const holidayBannerProps = shouldShowHolidayBanner
    ? buildBannerPropsWithDynamicContent(holidayBanner.fields, {
        linkUrl: prepayExtensionFlowLink,
      })
    : null;

  const handleBannerClick = () => {
    trackExtensionFlowStepEvent({
      stepName: EXTENSION_UPSELL_EVENT_STEPS.STARTED.name,
      url: window.location.href,
      bundleSize: '',
      platform: getPlatform(),
      stepNumber: EXTENSION_UPSELL_EVENT_STEPS.STARTED.stepNumber,
    });
    trackClickEvent(CTA_NAMES.EXTENSION_FLOW_STARTED);
  };

  return (
    <div data-testid="layout-active-subscription">
      {prepayBannerProps && (
        <div className="mb-6">
          <CmsBanner
            testId="prepay-banner-top"
            {...prepayBannerProps}
            onClickCta={handleBannerClick}
          />
        </div>
      )}
      {holidayBannerProps && (
        <div className="mb-6">
          <CmsBanner
            testId="holiday-banner-top"
            {...holidayBannerProps}
            onClickCta={handleBannerClick}
          />
        </div>
      )}

      {currentGroup && (
        <CurrentPlayKit
          t={t}
          group={currentGroup}
          noticeBanner={noticeBanner}
        />
      )}

      {shouldShowDiscountApplied && (
        <div className="mb-3 md:mb-4 p-4 md:p-6 bg-kelly-green-tint border border-success rounded">
          <p className="text-h-x5 md:text-h-x4 font-bold text-success">
            {t.discountApplied}
          </p>

          <p className="text-sm md:text-base">
            {interpolate(t.discountAppliedSubtext, {
              discountAmount,
            })}
          </p>
        </div>
      )}

      {nextGroups?.map((group, index) => (
        <NextPlayKit
          key={group.items?.[0].uuid}
          t={t}
          group={group}
          isFirstGroup={index === 0}
          hasManyGroups={
            nextGroups.filter((group) => group.isUpcomingOrderPreview)?.length >
            1
          }
        />
      ))}

      {visibleUpcomingPlayKits?.map(
        (upcomingKitGroup: TypeScheduledItemGroup, index: number) => (
          <UpcomingPlayKit
            key={upcomingKitGroup.items?.[0].uuid}
            t={t}
            group={upcomingKitGroup}
            isFirstGroup={index === 0}
          />
        ),
      )}

      {hasHiddenUpcomingKits && (
        <Button
          onClick={() => setShowHiddenKits(!showHiddenKits)}
          ctaText={showHiddenKits ? t.showLessKits : t.showMoreKits}
          size={BUTTON_SIZES.lg}
          style={BUTTON_STYLES.secondary}
        />
      )}
    </div>
  );
}

export default LayoutActiveSubscription;
